import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box,
  Autocomplete,
  TextField,
  Chip,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { getDbHeader } from "../../utils/util";

/**
 * Props:
 * - title: string
 * - onSelectionChange: function(selectedArray) => void
 *    Called whenever the user selects/unselects categories
 * - id: string | number
 */

const CategorySelectorAccordions = ({ title = "", onSelectionChange, id }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [expandedMainCategory, setExpandedMainCategory] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(false);

  // Fetch categories with cleanup
  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${API_ENDPOINT}event/get-product-categories`,
          {
            ...getDbHeader(id),
            signal: controller.signal,
          }
        );
        setData(response.data);
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error(err);
          setError("Failed to load categories");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => controller.abort();
  }, [id]);

  // Memoized selection updater (unchanged)
  const handleSelectionUpdate = useCallback(
    (newSelections) => {
      setSelectedCategories(newSelections);
      onSelectionChange?.(newSelections);
    },
    [onSelectionChange]
  );

  // Category check used by double/triple (unchanged)
  const isCategorySelected = useCallback(
    (category) =>
      selectedCategories.some(
        (sel) =>
          sel.MainCategory === category.MainCategory &&
          (sel.Category ?? null) === (category.Category ?? null) &&
          (sel.SubCategory ?? null) === (category.SubCategory ?? null)
      ),
    [selectedCategories]
  );

  // Toggle used by double/triple (unchanged)
  const handleCheckboxChange = useCallback(
    (category) => {
      const exists = selectedCategories.some(
        (sel) =>
          sel.MainCategory === category.MainCategory &&
          (sel.Category ?? null) === (category.Category ?? null) &&
          (sel.SubCategory ?? null) === (category.SubCategory ?? null)
      );

      handleSelectionUpdate(
        exists
          ? selectedCategories.filter(
              (sel) =>
                !(
                  sel.MainCategory === category.MainCategory &&
                  (sel.Category ?? null) === (category.Category ?? null) &&
                  (sel.SubCategory ?? null) === (category.SubCategory ?? null)
                )
            )
          : [...selectedCategories, category]
      );
    },
    [selectedCategories, handleSelectionUpdate]
  );

  // Memoized grouped data (unchanged)
  const { mainGroups, categoryGroups } = useMemo(() => {
    if (!data?.categories) return { mainGroups: {}, categoryGroups: {} };

    const groups = data.categories.reduce((acc, cat) => {
      const main = cat.MainCategory || "Unknown";
      if (!acc[main]) acc[main] = [];
      acc[main].push(cat);
      return acc;
    }, {});

    const catGroups =
      data.type === "triple"
        ? data.categories.reduce((acc, item) => {
            const main = item.MainCategory || "Unknown";
            const category = item.Category || "Uncategorized";

            if (!acc[main]) acc[main] = {};
            if (!acc[main][category]) acc[main][category] = [];
            acc[main][category].push(item);
            return acc;
          }, {})
        : {};

    return { mainGroups: groups, categoryGroups: catGroups };
  }, [data]);

  // ── SINGLE type ──────────────────────────────────────────────────────────────

  // One option per unique MainCategory
  const singleOptions = useMemo(() => {
    if (data?.type !== "single") return [];
    return Object.keys(mainGroups).map((main) => ({
      MainCategory: main,
      Category: null,
      SubCategory: null,
    }));
  }, [data, mainGroups]);

  // Current Autocomplete value — only single-type entries
  const singleValue = useMemo(
    () =>
      selectedCategories.filter(
        (sel) => sel.Category === null && sel.SubCategory === null
      ),
    [selectedCategories]
  );

  const handleSingleChange = useCallback(
    (_event, newValue) => {
      handleSelectionUpdate(newValue);
    },
    [handleSelectionUpdate]
  );

  // ── DOUBLE / TRIPLE render helpers (unchanged) ───────────────────────────────

  const getSelectedCount = useCallback(
    (items) => items.filter(isCategorySelected).length,
    [isCategorySelected]
  );

  const optionSx = {
    width: "100%",
    m: 0,
    px: 1,
    borderRadius: 1,
    alignItems: "flex-start",
    "&:hover": { bgcolor: "action.hover" },
    "& .MuiCheckbox-root": { pt: 0.75 },
    "& .MuiFormControlLabel-label": { pt: 1 },
  };

  const renderDouble = (items) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
        gap: 0.5,
      }}
    >
      {items.map((cat, idx) => (
        <FormControlLabel
          sx={optionSx}
          key={`${cat.MainCategory}-${cat.Category}-${idx}`}
          control={
            <Checkbox
              checked={isCategorySelected(cat)}
              onChange={() => handleCheckboxChange(cat)}
            />
          }
          label={<Typography variant="body2">{cat.Category || "N/A"}</Typography>}
        />
      ))}
    </Box>
  );

  const renderTriple = (mainCategory) => {
    const categories = categoryGroups[mainCategory] || {};

    return Object.entries(categories).map(([category, items]) => {
      const categoryKey = JSON.stringify([mainCategory, category]);
      const selectedCount = getSelectedCount(items);

      return (
        <Accordion
          key={categoryKey}
          expanded={expandedCategory === categoryKey}
          onChange={(_event, isExpanded) =>
            setExpandedCategory(isExpanded ? categoryKey : false)
          }
          disableGutters
          elevation={0}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: "8px !important",
            mb: 1,
            overflow: "hidden",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              minHeight: 48,
              bgcolor:
                expandedCategory === categoryKey
                  ? "action.selected"
                  : "background.paper",
              "& .MuiAccordionSummary-content": { my: 1 },
            }}
          >
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {category}
              </Typography>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mr: 1 }}
              >
                <Typography variant="caption" color="text.secondary" noWrap>
                  {items.length} {items.length === 1 ? "option" : "options"}
                </Typography>
                {selectedCount > 0 && (
                  <Chip
                    label={`${selectedCount} selected`}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1, maxHeight: 320, overflowY: "auto" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 0.5,
              }}
            >
              {items.map((subItem, subIdx) => (
                <FormControlLabel
                  sx={optionSx}
                  key={`${categoryKey}-${subItem.SubCategory}-${subIdx}`}
                  control={
                    <Checkbox
                      checked={isCategorySelected(subItem)}
                      onChange={() => handleCheckboxChange(subItem)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {subItem.SubCategory || "N/A"}
                    </Typography>
                  }
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  // ── UI states (unchanged) ────────────────────────────────────────────────────

  if (loading)
    return (
      <Box textAlign="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data?.categories?.length)
    return <Alert severity="info">No categories available</Alert>;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box my={2}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: title ? 1 : 0,
        }}
      >
        {title && (
          <Typography variant="h6" fontWeight="bold">
            {title}
          </Typography>
        )}
        {selectedCategories.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
            <Chip
              label={`${selectedCategories.length} selected`}
              color="primary"
              size="small"
            />
            <Button size="small" onClick={() => handleSelectionUpdate([])}>
              Clear
            </Button>
          </Box>
        )}
      </Box>

      {data.type === "single" ? (
        /* ── Multi-select Autocomplete for SINGLE ── */
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={singleOptions}
          value={singleValue}
          getOptionLabel={(option) => option.MainCategory}
          isOptionEqualToValue={(option, value) =>
            option.MainCategory === value.MainCategory
          }
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} style={{ marginRight: 8 }} />
              {option.MainCategory}
            </li>
          )}
          onChange={handleSingleChange}
          renderInput={(params) => (
            <TextField {...params} label="Select Main Categories" />
          )}
        />
      ) : (
        /* ── Accordion layout for DOUBLE / TRIPLE ── */
        Object.entries(mainGroups).map(([mainCategory, items]) => {
          const selectedCount = getSelectedCount(items);
          const categoryCount =
            data.type === "triple"
              ? Object.keys(categoryGroups[mainCategory] || {}).length
              : items.length;

          return (
            <Accordion
            key={`${mainCategory}-${data.type}`}
            expanded={expandedMainCategory === mainCategory}
            onChange={(_event, isExpanded) => {
              setExpandedMainCategory(isExpanded ? mainCategory : false);
              setExpandedCategory(false);
            }}
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: "divider",
              "&:not(:last-of-type)": { borderBottom: 0 },
              "&:before": { display: "none" },
              "&:first-of-type": {
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
              },
              "&:last-of-type": {
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ "& .MuiAccordionSummary-content": { my: 1.25 } }}
            >
              <Box
                sx={{
                  width: "100%",
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight="bold" noWrap>
                    {mainCategory}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {categoryCount}{" "}
                    {data.type === "triple"
                      ? categoryCount === 1
                        ? "category"
                        : "categories"
                      : categoryCount === 1
                      ? "option"
                      : "options"}
                  </Typography>
                </Box>
                {selectedCount > 0 && (
                  <Chip
                    label={`${selectedCount} selected`}
                    color="primary"
                    size="small"
                    sx={{ mr: 1 }}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: data.type === "triple" ? 1.5 : 2 }}>
              {data.type === "double" && renderDouble(items)}
              {data.type === "triple" && renderTriple(mainCategory)}
            </AccordionDetails>
            </Accordion>
          );
        })
      )}
    </Box>
  );
};

export default React.memo(CategorySelectorAccordions);
